import { useMemo, useState } from 'react'
import { Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { cn } from '@/lib/utils'
import { FORM_CATEGORY_LABELS, PROCEDURE_TYPE_LABELS, type ProcedureType } from '@/types/cgt'
import type { GroupFormOption, GroupFormOptions } from '@/api/resourceCenterApi'
import {
  EMPTY_FORM_FILTER,
  categoriesPresent,
  filterForms,
  procedureNames,
  proceduresForType,
  procedureTypesPresent,
  toggleAll,
  type FormPickerFilter,
  type LinkFilter,
} from '@/lib/assignmentGroups'

const ALL = '__all__'

const TABLE_ID = 'resource-group-forms'
const COLUMNS: TableColumnDef[] = [
  { id: 'select', header: 'Linked', locked: true, defaultWidth: 56 },
  { id: 'name', header: 'Form', locked: true, defaultWidth: 280, sortable: true },
  { id: 'category', header: 'Form type', defaultWidth: 140, sortable: true },
  { id: 'procedures', header: 'Procedures', defaultWidth: 320, sortable: true },
  { id: 'status', header: 'Status', defaultWidth: 120, sortable: true },
]

const SHOW_OPTIONS: { value: LinkFilter; label: string }[] = [
  { value: 'all', label: 'All forms' },
  { value: 'linked', label: 'Linked' },
  { value: 'unlinked', label: 'Not linked' },
]

/**
 * The group form picker (deck slide 5) in the system table: procedure type →
 * ONE procedure to see only its forms → form type, link status and search.
 * Ticking a row links the form. Controlled: the caller owns the selection and
 * decides when to save.
 */
export function FormLinkPicker({
  options,
  loading,
  linked,
  saved,
  onChange,
  canManage,
  initialShow = 'all',
  maxHeight,
}: {
  options: GroupFormOptions | undefined
  loading: boolean
  linked: string[]
  /** What is stored, to flag unsaved rows; omit when creating. */
  saved?: string[]
  onChange: (ids: string[]) => void
  canManage: boolean
  initialShow?: LinkFilter
  maxHeight?: string
}) {
  const [filter, setFilter] = useState<FormPickerFilter>({ ...EMPTY_FORM_FILTER, show: initialShow })
  const forms = options?.forms ?? []
  const procedures = options?.procedures ?? []
  const isLinked = new Set(linked)
  const isSaved = new Set(saved ?? [])

  const sortAccessors = useMemo<SortAccessors<GroupFormOption>>(
    () => ({
      name: f => f.name,
      category: f => FORM_CATEGORY_LABELS[f.category],
      procedures: f => procedureNames(f, procedures).join(', '),
      status: f => (linked.includes(f.familyId) ? 0 : 1),
    }),
    [procedures, linked],
  )
  const cols = useTableColumns(TABLE_ID, COLUMNS)
  const shown = useSortedRows(filterForms(forms, procedures, filter, linked), cols.sort, sortAccessors)
  const shownIds = shown.map(f => f.familyId)
  const allShownLinked = shown.length > 0 && shown.every(f => isLinked.has(f.familyId))
  const someShownLinked = shown.some(f => isLinked.has(f.familyId))
  const typeProcedures = proceduresForType(procedures, filter.procedureType)
  const filtered = JSON.stringify(filter) !== JSON.stringify({ ...EMPTY_FORM_FILTER, show: initialShow })
  const set = (patch: Partial<FormPickerFilter>) => setFilter({ ...filter, ...patch })

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap items-end gap-2'>
        <Labelled label='Procedure type'>
          <Select
            value={filter.procedureType ?? ALL}
            onValueChange={v => set({ procedureType: v === ALL ? null : (v as ProcedureType), procedureIds: [] })}
          >
            <SelectTrigger className='h-9 w-44 rounded-lg text-sm' aria-label='Procedure type'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All types</SelectItem>
              {procedureTypesPresent(procedures).map(t => (
                <SelectItem key={t} value={t}>
                  {PROCEDURE_TYPE_LABELS[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Labelled>
        <Labelled label='Procedure'>
          <SearchableSelect
            className='w-64'
            buttonClassName='h-9 rounded-lg text-sm'
            value={filter.procedureIds[0] ?? ALL}
            onChange={v => set({ procedureIds: v === ALL ? [] : [v] })}
            options={[
              {
                value: ALL,
                label: filter.procedureType ? `All ${PROCEDURE_TYPE_LABELS[filter.procedureType]}` : 'All procedures',
              },
              ...typeProcedures.map(p => ({ value: p.familyId, label: p.name })),
            ]}
            placeholder='All procedures'
          />
        </Labelled>
        <Labelled label='Show'>
          <div className='inline-flex h-9 items-center rounded-lg bg-muted p-0.5'>
            {SHOW_OPTIONS.map(o => (
              <button
                key={o.value}
                type='button'
                aria-pressed={filter.show === o.value}
                onClick={() => set({ show: o.value })}
                className={cn(
                  'h-8 rounded-md px-3 text-xs font-medium transition-all',
                  filter.show === o.value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
        </Labelled>
        <Labelled label='Search' className='min-w-52 flex-1'>
          <div className='relative'>
            <Search className='pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
            <Input
              value={filter.search}
              onChange={e => set({ search: e.target.value })}
              placeholder='Form name'
              aria-label='Search forms'
              className='h-9 rounded-lg pl-8 text-sm'
            />
          </div>
        </Labelled>
        {filtered && (
          <Button size='sm' variant='ghost' className='h-9' onClick={() => setFilter({ ...EMPTY_FORM_FILTER, show: initialShow })}>
            <X className='mr-1 h-3.5 w-3.5' /> Clear
          </Button>
        )}
      </div>

      <div className='flex flex-wrap items-center gap-1.5'>
        <span className='mr-1 text-xs font-medium text-muted-foreground'>Form type</span>
        <Chip on={filter.categories.length === 0} onClick={() => set({ categories: [] })}>
          All
        </Chip>
        {categoriesPresent(forms).map(c => (
          <Chip
            key={c}
            on={filter.categories.includes(c)}
            onClick={() =>
              set({
                categories: filter.categories.includes(c)
                  ? filter.categories.filter(x => x !== c)
                  : [...filter.categories, c],
              })
            }
          >
            {FORM_CATEGORY_LABELS[c]}
          </Chip>
        ))}
      </div>

      <p className='text-xs text-muted-foreground'>
        <span className='font-semibold text-foreground'>{linked.length}</span> selected ·{' '}
        {loading ? 'loading forms…' : `${shown.length} of ${forms.length} shown`}
      </p>

      {!loading && shown.length === 0 ? (
        <div className='rounded-xl border border-dashed py-12 text-center text-sm text-muted-foreground'>
          {filter.show === 'linked' && linked.length === 0
            ? 'No forms linked yet — show “All forms” to pick some.'
            : 'No forms match these filters.'}
        </div>
      ) : (
        <div className='overflow-auto rounded-xl border' style={maxHeight ? { maxHeight } : undefined}>
          <Table>
            <TableHeader className='sticky top-0 z-10'>
              <TableRow>
                {cols.visible.map(({ def }) => (
                  <ResizableTableHead key={def.id} columnId={def.id} columns={cols} label={def.header} resizable={def.id !== 'select'}>
                    {def.id === 'select' ? (
                      <Checkbox
                        aria-label='Select all shown forms'
                        disabled={!canManage || shown.length === 0}
                        checked={allShownLinked ? true : someShownLinked ? 'indeterminate' : false}
                        onCheckedChange={v => onChange(toggleAll(linked, shownIds, v === true))}
                      />
                    ) : (
                      def.header
                    )}
                  </ResizableTableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map(f => {
                const on = isLinked.has(f.familyId)
                const procs = procedureNames(f, procedures)
                const changed = saved !== undefined && on !== isSaved.has(f.familyId)
                return (
                  <TableRow
                    key={f.familyId}
                    className={cn('cursor-pointer', on && 'bg-primary/[0.04]')}
                    onClick={() => canManage && onChange(on ? linked.filter(id => id !== f.familyId) : [...linked, f.familyId])}
                  >
                    <ColumnCells
                      columns={cols}
                      cells={{
                        select: (
                          <TableCell onClick={e => e.stopPropagation()}>
                            <Checkbox
                              aria-label={`Link ${f.name}`}
                              disabled={!canManage}
                              checked={on}
                              onCheckedChange={v =>
                                onChange(v === true ? [...linked, f.familyId] : linked.filter(id => id !== f.familyId))
                              }
                            />
                          </TableCell>
                        ),
                        name: <TableCell className='font-medium'>{f.name}</TableCell>,
                        category: <TableCell>{FORM_CATEGORY_LABELS[f.category]}</TableCell>,
                        procedures: (
                          <TableCell className='text-muted-foreground'>
                            <span className='line-clamp-2' title={procs.join(', ')}>
                              {procs.length > 0 ? procs.join(', ') : '—'}
                            </span>
                          </TableCell>
                        ),
                        status: (
                          <TableCell>
                            <span
                              className={cn(
                                'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                                on ? 'bg-emerald-50 text-emerald-700' : 'bg-muted text-muted-foreground',
                                changed && 'ring-1 ring-amber-300',
                              )}
                              title={changed ? 'Not saved yet' : undefined}
                            >
                              {on ? 'Linked' : 'Not linked'}
                            </span>
                          </TableCell>
                        ),
                      }}
                    />
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

function Labelled({ label, className, children }: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={cn('space-y-1', className)}>
      <span className='block text-xs font-medium text-muted-foreground'>{label}</span>
      {children}
    </div>
  )
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type='button'
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        on ? 'border-primary bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}
